/**
 * The tax preview (piece 25; decision 0644): what a sales document's form asks
 * while it is being typed, and what the answer draws. 🔒 The answer is
 * `calculateTax`'s own, reached through the same server function the save
 * calls, so the figure a person watches is the figure the save then stores.
 *
 * 🔨 The preview writes nothing. It is the save's arithmetic without the save.
 */
import type { TaxGap, TaxZeroReason } from './calculateTax';
import type { TaxJurisdictionTotal } from './localTax';
import { formatRatePercent, hasNoStatewideSalesTax } from './rateTable';
import { US_STATE_NAMES } from '../records/taxExemption';
import { fill, t } from '../strings';

/** The five documents whose forms compute tax live. The Purchase Order is not one: purchase-side tax is use tax, not this answer. */
export const TAX_PREVIEW_KINDS = ['Quote', 'SalesOrder', 'Invoice', 'CreditNote', 'ChangeOrder'] as const;
export type TaxPreviewKind = (typeof TAX_PREVIEW_KINDS)[number];

/**
 * 🔒 The preview is gated on the module the document's own routes read
 * (decision 0644), so a person who can open the form can see its tax and
 * nobody else can ask for it.
 */
export const TAX_PREVIEW_MODULES: Readonly<Record<TaxPreviewKind, 'Quote' | 'SalesOrder' | 'Invoice' | 'Accounting'>> = {
  Quote: 'Quote',
  SalesOrder: 'SalesOrder',
  ChangeOrder: 'SalesOrder',
  Invoice: 'Invoice',
  CreditNote: 'Accounting',
};

/** One line as the form holds it, in the shape its save sends; `key` is the form's own row id, echoed back. */
export interface TaxPreviewLine {
  key: string;
  itemId?: string | null;
  taxCategoryId?: string | null;
  quantity: number;
  unitPriceCents?: number | null;
  discountType?: string | null;
  discountValue?: number | null;
  /** A Credit Note's line names the Invoice line it credits, and its tax is that line's share. */
  sourceLineId?: string | null;
}

/** What a form sends: the kind, what it is standing on, the date and the lines. */
export interface TaxPreviewRequest {
  documentType: TaxPreviewKind;
  /** The stored document being edited, where one exists; its exemption is read from it exactly as its save reads it. */
  documentId?: string | null;
  /** The Sales Order an Invoice bills, or a Change Order amends. */
  salesOrderId?: string | null;
  /** The Invoice a Credit Note credits. */
  invoiceId?: string | null;
  shipToSiteId?: string | null;
  /** A Quote's Bill-To, which its exemption snapshot falls back to where nothing ships (decision 0087); the tax itself never reads it. */
  billToSiteId?: string | null;
  /** The document's own calendar date where it carries one; today where it does not. */
  documentDate?: string | null;
  lines: TaxPreviewLine[];
}

export interface TaxPreviewLineAnswer {
  key: string;
  taxCents: number;
  ratePercent: number;
  zeroReason: TaxZeroReason | null;
}

/** 🔒 The totals box reads these four and nothing it computes itself (decision 0644). */
export interface TaxPreviewAnswer {
  lines: TaxPreviewLineAnswer[];
  /** The ship-to state the calculation read; null where the document names no ship-to with a state. */
  stateCode: string | null;
  /** The rate charged, where any line was charged one. */
  ratePercent: number | null;
  /** The gross sum of the lines, before their discounts, so the box foots the way the printed sheet does (decision 0560). */
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  /** Why the tax came out at nothing, where every taxed line did; null where something was charged or nothing was asked. */
  zeroReason: TaxZeroReason | null;
  /**
   * 🔒 One row per jurisdiction, the state first, as the save stores them
   * (decision 0738). Empty where nothing was charged, and the box then draws its
   * one Tax row with the reason beneath it.
   */
  rows: TaxJurisdictionTotal[];
  /** Why the ship-to is headed for the review report, where it is (decision 0736); the box says so in its grey line. */
  reportNote: TaxReportNote | null;
  /**
   * 🔒 Each category on the document whose state's treatment is Default, or
   * which no rule row answers, with its note (decision 0904). Its lines were
   * charged at the standard rate and the pairing is on the review report; the
   * box says so in its grey line. An answer from before it carried them has
   * none, and draws nothing.
   */
  reviews?: TaxReviewNote[];
  /**
   * 🔒 Decision 7460: the ship-to's address is only Estimated in a state with local tax, so the document is held. The sentence names the
   * address and what to press; the tax above is the state alone and is not final. Null or absent where nothing holds it.
   */
  hold?: TaxHold | null;
}

/** 🔒 The sentence a held document shows and is refused with, naming the address (decision 7460). */
export function taxHoldSentence(address: string): string {
  return fill(t('tax.hold.sentence'), { address });
}

/** The hold a document's tax is under (decision 7460): the sentence that is its refusal and its totals line, and the Location it points at. */
export interface TaxHold {
  sentence: string;
  locationId: string;
}

/** One pairing a document's lines met that needs review: the category, its note, and whether anybody answered at all. */
export interface TaxReviewNote {
  taxCategoryName: string;
  /** The Default treatment's note, a person's or the research's words; null where it carries none or nobody answered. */
  note: string | null;
  /** False where no rule row answers the pairing, so the line reads that nobody has answered. */
  answered: boolean;
}

/** The two location cases the calculation reports: no county entered, or a Partial state's lookup nobody confirmed. */
export type TaxReportNote = 'county-not-entered' | 'jurisdiction-not-confirmed' | 'address-estimated';

/** The location case the calculation's gaps name, the missing county first because it is the one a person can fix at once. */
export function taxReportNoteOf(gaps: readonly Pick<TaxGap, 'kind'>[]): TaxReportNote | null {
  // 🔒 An Estimated ship-to is named before any other, because Verified by User is the one fix (decision 7460).
  if (gaps.some((gap) => gap.kind === 'address-estimated')) return 'address-estimated';
  if (gaps.some((gap) => gap.kind === 'county-not-entered')) return 'county-not-entered';
  if (gaps.some((gap) => gap.kind === 'jurisdiction-not-confirmed')) return 'jurisdiction-not-confirmed';
  return null;
}

/**
 * 🔒 The grey line a case headed for the report carries (decision 0736), so a
 * state-only figure in a state with local tax is never silent.
 */
export function taxReportSentence(answer: Pick<TaxPreviewAnswer, 'stateCode' | 'reportNote'> & Partial<Pick<TaxPreviewAnswer, 'hold'>>): string | null {
  const state = answer.stateCode ?? '';
  switch (answer.reportNote) {
    case 'address-estimated':
      // 🔒 The hold's own sentence names the address; without it the generic one still says what to press (decision 7460).
      return `${answer.hold?.sentence ?? t('tax.hold.generic')} ${t('tax.hold.notFinal')} ${t('tax.preview.onReport')}`;
    case 'county-not-entered':
      return `${t('tax.preview.countyNotEntered')} ${state}. ${t('tax.preview.onReport')}`;
    case 'jurisdiction-not-confirmed':
      return `${state} ${t('tax.preview.jurisdictionNotConfirmed')} ${t('tax.preview.onReport')}`;
    default:
      return null;
  }
}

/**
 * 🔒 The grey line a pairing that needs review carries (decision 0904), one
 * per category: *Charged at the standard rate: Repair labor on equipment in
 * Florida.*, then its note, then *On the review report.* A pairing nobody
 * answered reads *Charged at the standard rate: nobody has answered how Florida
 * taxes Repair labor on equipment. On the review report.*
 */
export function taxReviewSentences(answer: Pick<TaxPreviewAnswer, 'stateCode' | 'reviews'>): string[] {
  const state = answer.stateCode ? US_STATE_NAMES[answer.stateCode.toUpperCase()] ?? answer.stateCode : '';
  return (answer.reviews ?? []).map((review) => {
    if (!review.answered) return `${t('tax.preview.chargedAtStandard')} ${t('tax.preview.nobodyAnswered')} ${state} ${t('tax.preview.taxes')} ${review.taxCategoryName}. ${t('tax.preview.onReviewReport')}`;
    const note = review.note?.trim() || null;
    const closed = note === null ? '' : ` ${/[.!?]$/.test(note) ? note : `${note}.`}`;
    return `${t('tax.preview.chargedAtStandard')} ${review.taxCategoryName} ${t('tax.preview.in')} ${state}.${closed} ${t('tax.preview.onReviewReport')}`;
  });
}

/**
 * The document's one reason, read off its lines: nothing where any line was
 * charged, and otherwise the first reason a line gave. Every line of one
 * document shares its jurisdiction, so the lines disagree only where a
 * category is not taxed, and the jurisdiction's reason is the one worth saying.
 */
export function documentZeroReason(lines: readonly { taxCents: number; zeroReason: TaxZeroReason | null }[]): TaxZeroReason | null {
  if (lines.length === 0 || lines.some((line) => line.taxCents !== 0)) return null;
  const reasons = lines.map((line) => line.zeroReason).filter((reason): reason is TaxZeroReason => reason !== null);
  const jurisdiction = reasons.find((reason) => reason !== 'category-not-taxable');
  return jurisdiction ?? reasons[0] ?? null;
}

/** A rate as a person reads it: 6, 6.25 and 6.875 rather than 6.000, by the one rate-table rule. */
export const previewRatePercent = formatRatePercent;

/**
 * 🔒 The words ahead of a jurisdiction row's base (decision 0738): *Pennsylvania
 * sales tax · 6% on*, which the screen follows with the base drawn as money.
 */
export function taxJurisdictionRowLead(row: { label: string; ratePercent: number }): string {
  return `${row.label} · ${previewRatePercent(row.ratePercent)} ${t('tax.preview.on')}`;
}

/**
 * The label the totals box puts on its Tax row: *PA sales tax 6%*, or the
 * plain word where no state was read (decision 0644).
 */
export function taxRowLabel(answer: Pick<TaxPreviewAnswer, 'stateCode' | 'ratePercent'>): string {
  if (!answer.stateCode) return t('tax.preview.tax');
  if (answer.ratePercent === null) return `${answer.stateCode} ${t('tax.preview.salesTax')}`;
  return `${answer.stateCode} ${t('tax.preview.salesTax')} ${previewRatePercent(answer.ratePercent)}`;
}

/**
 * 🔒 The muted line under a Tax row of nothing, so a zero is never silent
 * (decision 0644; piece 25). *No registration in force for PA on this date*,
 * *No sales tax rate is entered for PA*, and the calculation's other reasons.
 * Null where the tax is not nothing, or where there is nothing to explain.
 */
export function taxZeroNote(answer: Pick<TaxPreviewAnswer, 'stateCode' | 'zeroReason' | 'ratePercent' | 'taxCents'>): string | null {
  if (answer.taxCents !== 0) return null;
  const state = answer.stateCode ?? '';
  switch (answer.zeroReason) {
    case null:
      // A state with no statewide sales tax charges nothing with no reason to give, and still says why.
      return state && hasNoStatewideSalesTax(state) ? `${t('tax.preview.noStatewideTax')} ${state}` : null;
    case 'no-state':
      return t('tax.preview.noState');
    case 'not-registered':
      return `${t('tax.preview.noRegistration')} ${state} ${t('tax.preview.onThisDate')}`;
    case 'no-rate-in-force':
      return `${t('tax.preview.noRate')} ${state}`;
    case 'exempt':
      return `${t('tax.preview.exempt')} ${state}`;
    case 'category-not-taxable':
      return `${state} ${t('tax.preview.categoryNotTaxable')}`;
  }
}
